import './MastersTab.scss';
import '../MastersAndPartnersTabs.scss';
import { useQuery } from '@tanstack/react-query';
import Skeleton from '@trackman/web-shared-components/components/LoadingPlaceholder/Skeleton';
import Modal from '@trackman/web-shared-components/components/Modal/Modal';
import { Paragraph } from 'components/TextComps/Paragraph';
import Subtitle from 'components/TextComps/Subtitle';
import { useServices } from 'index';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { sortByString } from 'utilities/dataSorters';
import { MastersAndPartnersCard, MastersAndPartnersLoadingCard } from '../MastersAndPartnersCard/MastersAndPartnersCard';
import { PartnerOrMaster } from 'types/partner';

export const MastersTab = () => {
  const { t } = useTranslation();
  const mastersService = useServices().mastersService;

  const { data: mastersList, isPending } = useQuery({
    queryKey: ['masters'],
    queryFn: async () => {
      const response = await mastersService.getMasters();
      return sortByString(response, 'fullName', 'ASCENDING');
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const [openModalId, setOpenModalId] = useState<string | null>(null);

  return (
    <div className='masters-and-partners-tab'>
      <div className='masters-and-partners-header'>
        <h2>{t('MasterGalleryTitle', { ns: 'mastersGallery' })}</h2>
        {isPending ? (
          <Skeleton variant='text' width={200} sx={{ height: '18px' }} />
        ) : (
          <Paragraph>
            <Trans
              i18nKey='LearnMoreAboutMasters'
              ns='mastersGallery'
              defaults='Learn more about the <bold>{{mastersCount}} people</bold>  who have received the Trackman Masters status'
              values={{ mastersCount: mastersList?.length }}
              components={{ bold: <strong /> }}
            />
          </Paragraph>
        )}
      </div>
      <div className='masters-and-partners-gallery'>
        {!isPending
          ? mastersList?.map((master, i) => (
              <>
                <MastersAndPartnersCard
                  key={`master-${i}`}
                  name={master.fullName}
                  imgUrl={master.imageUrl}
                  description={master.description}
                  customClasses='master-card'
                  handleSetIsModalOpen={() => setOpenModalId(master.id)}
                />
                {openModalId === master.id ? (
                  <MasterModal
                    master={mastersList?.find((master) => master.id === openModalId)}
                    open={true}
                    handleClose={() => setOpenModalId(null)}
                  />
                ) : (
                  <></>
                )}
              </>
            ))
          : Array.from(Array(10)).map((_, i) => <MastersAndPartnersLoadingCard key={`loading-master-${i}`} />)}
      </div>
    </div>
  );
};

const MasterModal = ({ master, open, handleClose }: { master: PartnerOrMaster; open: boolean; handleClose: () => void }) => {
  if (!master) {
    return null;
  }

  return (
    <Modal open={open} onClose={handleClose} className='master-modal'>
      <div className='master-modal-content'>
        <div className='master-img-container'>
          <img src={master.imageUrl} alt={`${master.fullName}`} />
          <Subtitle>
            <>{master.fullName}</>
          </Subtitle>
        </div>
        <div className='master-description-container'>{master.description}</div>
      </div>
    </Modal>
  );
};
